import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import {useInView} from 'react-intersection-observer';

import {assertAndContinue} from 'Asserts';
import cellValueToUserFacingString from 'data-frames/util/cellValueToUserFacingString';
import {getHydratedBlob} from 'data-frames/util/getHydratedProperty';
import {isLeafTypePrimitive, isSubType, LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {tryDeserializeDate, tryDeserializeDatetime} from 'data-types/util/Deserializers';
import QueryingBlobLinkList from 'insights/analytics-visualizations/blobs/QueryingBlobLinkList';
import {BlobLinkList} from 'notebook/data-tables/BlobLinkList';
import {QueryingExternalFilePaneContent} from 'pipelines/files/ExternalFilePaneContent';
import {restBlobToGraphQL} from 'schemas/tags/BlobUtils';
import BenchChip from 'ui-library/chips/BenchChip';
import {ChipSize} from 'ui-library/chips/Chip';
import {CHIP_DISPLAY_LIMIT_TABLE_ROW} from 'ui-library/chips/ChipConstants';
import ChipList from 'ui-library/chips/ChipList';
import LoadingChip from 'ui-library/chips/LoadingChip';
import DatetimeValue from 'ui-library/DatetimeValue';
import DateValue from 'ui-library/DateValue';
import UrlAwareText from 'ui-library/UrlAwareText';
import UnitAwareNumberTagValueViewer from 'units/UnitAwareNumberTagValueViewer';
import APIUtil from 'util/APIUtil';
import removeNulls from 'util/removeNulls';
import {JSONValue} from 'util/Types';

import {DataFrameColumnDef, HydratedProperties, isColumnTypeNumerical} from '../DataFrameTypes';
import {cellToBlobIds, cellToChipItemIds, cellToIds} from './cellToIds';
import {DataFrameTableMode} from './DataFrameTableConstants';

const CHIP_SIZE = ChipSize.TABLE_CELL;
/** @package */
export const SCRATCH_FILE_WRAPPER_SIZE = 150;
/** @package */
export const SCRATCH_FILE_LIST_GAP = 10;

export const MODE_TO_SHOULD_RENDER_CHIPS: {[key in DataFrameTableMode]: boolean} = {
  [DataFrameTableMode.RICH]: true,
  [DataFrameTableMode.TEXT]: false,
  [DataFrameTableMode.RAW_WAREHOUSE]: false,
};

const MODE_TO_SHOULD_RENDER_BLOBS: {[key in DataFrameTableMode]: boolean} = {
  [DataFrameTableMode.RICH]: true,
  [DataFrameTableMode.TEXT]: false,
  [DataFrameTableMode.RAW_WAREHOUSE]: false,
};

const MODE_TO_SHOULD_RENDER_DATE_CHIPS: {[key in DataFrameTableMode]: boolean} = {
  [DataFrameTableMode.RICH]: true,
  [DataFrameTableMode.TEXT]: false,
  [DataFrameTableMode.RAW_WAREHOUSE]: false,
};

const MODE_TO_SHOULD_RENDER_FILES: {[key in DataFrameTableMode]: boolean} = {
  [DataFrameTableMode.RICH]: true,
  [DataFrameTableMode.TEXT]: false,
  [DataFrameTableMode.RAW_WAREHOUSE]: false,
};

const MODE_TO_SHOULD_RENDER_UNITS: {[key in DataFrameTableMode]: boolean} = {
  [DataFrameTableMode.RICH]: true,
  [DataFrameTableMode.TEXT]: false,
  [DataFrameTableMode.RAW_WAREHOUSE]: false,
};

function renderChipsIfPresent(
  cellValue: JSONValue,
  delayChipRendering: boolean,
  hydratedProperties: HydratedProperties
): JSX.Element | null {
  // Render chips for all chips, even those that aren't hydrated.
  const chipItemIds = cellToChipItemIds(cellValue);

  if (chipItemIds == null) {
    return null;
  }

  if (delayChipRendering) {
    return <LoadingChip size={CHIP_SIZE} />;
  } else {
    return chipItemIds.length === 1 ? (
      <BenchChip id={chipItemIds[0]} size={CHIP_SIZE} />
    ) : (
      <ChipList
        chipSize={CHIP_SIZE}
        isVertical={false}
        itemIds={chipItemIds}
        maxChips={CHIP_DISPLAY_LIMIT_TABLE_ROW}
      />
    );
  }
}

/**
 * Render blobs.
 *
 * If `hydratedProperties` is populated, only hydrated blobs will be rendered.
 *
 * If `hydratedProperties` is empty, we assume that hydration is not intended and render all blobs IDs.
 *
 * Because UUIDs can be used for many things beside blobs, this should only be called if context dictates that
 * the values are meant to be blob IDs.
 *
 * @returns Rendered blobs, or null if cell shouldn't be rendered as blobs
 */
function renderBlobsIfPresent(
  cellValue: JSONValue,
  hydratedProperties: HydratedProperties
): JSX.Element | null {
  const blobIds = cellToBlobIds(cellValue);
  if (!blobIds) {
    return null;
  }

  const useHydratedProperties =
    Object.values(hydratedProperties.blobDicts).length > 0 ||
    Object.values(hydratedProperties.apiIdentifiedObjects).length > 0;

  if (useHydratedProperties) {
    const hydratedBlobs = removeNulls(blobIds.map((blobId) => getHydratedBlob(blobId, hydratedProperties)));

    // Do not render partially-hydrated cells as blobs
    if (hydratedBlobs.length < blobIds.length) {
      return null;
    }

    return (
      <BlobLinkList
        blobs={hydratedBlobs.map((blob) => restBlobToGraphQL(blob))}
        chipSize={CHIP_SIZE}
        blobClassName={css(styles.blobLink)}
      />
    );
  } else {
    return <QueryingBlobLinkList blobIds={blobIds} chipSize={CHIP_SIZE} />;
  }
}

function renderDateChipsIfPresent(cellValue: JSONValue, column: DataFrameColumnDef): JSX.Element | null {
  if (!isSubType(column.type.leafType, LeafType.DATETIME)) {
    return null;
  }

  if (column.type.leafType === LeafType.DATE) {
    const deserializeResult = tryDeserializeDate(cellValue);
    return deserializeResult.deserialized ? (
      <DateValue dateValue={deserializeResult.value} canHighlight={true} />
    ) : null;
  } else if (column.type.leafType === LeafType.DATETIME) {
    const deserializeResult = tryDeserializeDatetime(cellValue);
    return deserializeResult.deserialized ? (
      <DatetimeValue datetimeValue={deserializeResult.value} canHighlight={true} />
    ) : null;
  } else {
    assertAndContinue(false, `Unexpected date type ${column.type.leafType}`, {column});
    return null;
  }
}

function ScratchFilesContent({
  cellValue,
  column,
  hydratedProperties,
  tableMode,
}: Omit<DataFrameTableCellProps, 'delayChipRendering'>): JSX.Element | null {
  const scratchFileIds = cellToIds(
    cellValue,
    (value) => typeof value === 'string' && APIUtil.isScratchFile(value)
  );

  const {ref, inView} = useInView();

  return (
    <div ref={ref} className={css(styles.scratchFileList)}>
      {!scratchFileIds || !inView ? (
        // Make off view cells approximately the same size as in view cells to improve the scrolling experience.
        // Otherwise, while scrolling, the cells will change size, which feels confusing because rows go up and down.
        <div
          style={{
            height:
              SCRATCH_FILE_WRAPPER_SIZE * (scratchFileIds?.length ?? 1) +
              SCRATCH_FILE_LIST_GAP * ((scratchFileIds?.length ?? 1) - 1),
            width: SCRATCH_FILE_WRAPPER_SIZE,
          }}
        >
          {renderDefaultCellContent(cellValue, column, hydratedProperties, tableMode)}
        </div>
      ) : (
        scratchFileIds.map((scratchFileId) => (
          <div key={scratchFileId} className={css(styles.scratchFileWrapper)}>
            <QueryingExternalFilePaneContent fileId={scratchFileId} />
          </div>
        ))
      )}
    </div>
  );
}

function renderUnitsIfPresent(userFacingString: string, column: DataFrameColumnDef): JSX.Element | null {
  if (!isColumnTypeNumerical(column.type) || column.type.unit?.unitApiId == null) {
    return null;
  }

  return (
    <div className={css(styles.textAlignRight)}>
      <UnitAwareNumberTagValueViewer
        value={userFacingString}
        unitApiIdentifier={column.type.unit.unitApiId}
      />
    </div>
  );
}

function renderDefaultCellContent(
  cellValue: JSONValue,
  column: DataFrameColumnDef,
  hydratedProperties: HydratedProperties,
  tableMode: DataFrameTableMode
): JSX.Element {
  return (
    <UrlAwareText
      className={css(styles.textCellWrapper)}
      textValue={cellValueToUserFacingString(cellValue, column, hydratedProperties, tableMode)}
    />
  );
}

export interface DataFrameTableCellProps {
  cellValue: JSONValue;
  column: DataFrameColumnDef;
  tableMode: DataFrameTableMode;
  hydratedProperties: HydratedProperties;
  delayChipRendering: boolean;
}

function DataFrameTableCellValue(props: DataFrameTableCellProps): JSX.Element {
  if (props.column.type.leafType === LeafType.SCRATCH_FILE && MODE_TO_SHOULD_RENDER_FILES[props.tableMode]) {
    return <ScratchFilesContent {...props} />;
  }

  if (MODE_TO_SHOULD_RENDER_CHIPS[props.tableMode] && !isLeafTypePrimitive(props.column.type.leafType)) {
    const chipElement = renderChipsIfPresent(
      props.cellValue,
      props.delayChipRendering,
      props.hydratedProperties
    );
    if (chipElement) {
      return chipElement;
    }
  }

  if (props.column.type.leafType === LeafType.BLOB && MODE_TO_SHOULD_RENDER_BLOBS[props.tableMode]) {
    const blobElement = renderBlobsIfPresent(props.cellValue, props.hydratedProperties);
    if (blobElement) {
      return blobElement;
    }
  }

  if (MODE_TO_SHOULD_RENDER_DATE_CHIPS[props.tableMode]) {
    const dateElement = renderDateChipsIfPresent(props.cellValue, props.column);
    if (dateElement) {
      return dateElement;
    }
  }

  if (MODE_TO_SHOULD_RENDER_UNITS[props.tableMode]) {
    const unitElement = renderUnitsIfPresent(
      cellValueToUserFacingString(props.cellValue, props.column, props.hydratedProperties, props.tableMode),
      props.column
    );
    if (unitElement) {
      return unitElement;
    }
  }

  return renderDefaultCellContent(props.cellValue, props.column, props.hydratedProperties, props.tableMode);
}

export default React.memo(DataFrameTableCellValue);

const styles = StyleSheet.create({
  textCellWrapper: {
    display: 'block',
    // We specify a maxWidth to handle content such as sequences which can be arbitrarily long.
    // We still want the UI to be usable in those cases.
    maxWidth: 500,
    overflowX: 'hidden',
    // This is not strictly required but is a safeguard to ensure scrollbars are not unintentionally added
    // to each cell if the y content overflows. Having scrollbars rendered on every cell (due to a line
    // height error) has caused visual and performance issues in the past. The bug is fixed by using the style
    // FONT_PROXIMA_13 in the DataFrameTable component.
    overflowY: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  blobLink: {
    width: 200,
  },
  textAlignRight: {
    textAlign: 'right',
  },
  scratchFileWrapper: {
    height: SCRATCH_FILE_WRAPPER_SIZE,
    width: SCRATCH_FILE_WRAPPER_SIZE,
    overflow: 'scroll',
    position: 'relative',
    boxSizing: 'border-box',
  },
  scratchFileList: {
    display: 'flex',
    flexDirection: 'column',
    gap: SCRATCH_FILE_LIST_GAP,
  },
});
